import { useEffect, useSyncExternalStore } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { workspaceStore, type WorkspaceState } from './store'

const STORAGE_KEY = 'v2:workspace'

export function useWorkspaceState(): WorkspaceState {
  return useSyncExternalStore(workspaceStore.subscribe, workspaceStore.getState)
}

/**
 * Headless controller mounted once in the shell. Owns:
 * - cold hydrate from v2:workspace + debounced persistence
 * - the desktop boundary watcher (≥1024px AND pointer:fine — R9)
 * - two-way floor↔router sync (pop-out sends the floor Home; floor
 *   navigation to a windowed path absorbs the window)
 */
export function WorkspaceController() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const state = useWorkspaceState()

  // Cold hydrate once, then correct for the real viewport.
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY)
      if (raw) workspaceStore.hydrate(raw)
    } catch {
      /* ignore */
    }
    workspaceStore.setViewport(window.innerWidth, window.matchMedia('(pointer: fine)').matches)
  }, [])

  // Debounced persistence of every store change.
  useEffect(() => {
    let t: number | null = null
    const unsub = workspaceStore.subscribe(() => {
      if (t) window.clearTimeout(t)
      t = window.setTimeout(() => {
        try {
          window.localStorage.setItem(STORAGE_KEY, workspaceStore.serialize())
        } catch {
          /* quota / private mode */
        }
      }, 250)
    })
    return () => {
      unsub()
      if (t) window.clearTimeout(t)
    }
  }, [])

  // Desktop boundary: resize + pointer-capability changes.
  useEffect(() => {
    const mq = window.matchMedia('(pointer: fine)')
    const update = () => workspaceStore.setViewport(window.innerWidth, mq.matches)
    window.addEventListener('resize', update)
    mq.addEventListener('change', update)
    return () => {
      window.removeEventListener('resize', update)
      mq.removeEventListener('change', update)
    }
  }, [])

  // Router → store: the floor navigated (store absorbs windowed paths).
  useEffect(() => {
    workspaceStore.floorNavigated(pathname)
  }, [pathname])

  // Store → router: pop-out (and friends) move the floor. CRITICAL: read
  // the store imperatively — the subscribed snapshot is one commit stale
  // right after a router-led change (the effect above just updated the
  // store), and comparing against the stale value navigates BACK to the
  // previous route, looping the router (the V2 "page hopping" bug).
  useEffect(() => {
    const current = workspaceStore.getState().floorPath
    if (current !== pathname) navigate(current)
  }, [state.floorPath, pathname, navigate])

  return null
}
